import { kioskLocation } from "../../App/kioskRoutes";
import { useState, useEffect, useRef, useCallback } from "react";
import Backend from "../../utils/api.js";
import { useHistory, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Flex,
  Avatar,
  Image,
  Button,
  Link,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { Global, css } from "@emotion/react";
import { useKiosk } from "../../Contexts/KioskContext";
import { useAnimationToggle } from "../../Contexts/AnimationContext";
import { FRONTEND_V2_BETA, useFeatureFlag } from "../../Contexts/FeatureFlagsContext";
import { useAppToast } from "../../Components/AppToast.jsx";
import { getGravatarUrl } from "../../utils/gravatar";
import pinpointLogo from "../../assets/pinpoint_no_bg.svg";
import { OUTER_LEN, PIN_LEN, VB, OUTER_D, PIN_D, strokeStyle } from "../../assets/pinpointLogoPaths";

const DURATION = "1.5s";

const logoHoverStyles = css`
  @keyframes navDrawOuter {
    0% { stroke-dashoffset: ${OUTER_LEN}; }
    25% { stroke-dashoffset: 0; }
    70% { stroke-dashoffset: 0; }
    88% { stroke-dashoffset: 0; }
    100% { stroke-dashoffset: 0; }
  }
  @keyframes navDrawPin {
    0% { stroke-dashoffset: ${PIN_LEN}; }
    25% { stroke-dashoffset: 0; }
    70% { stroke-dashoffset: 0; }
    88% { stroke-dashoffset: 0; }
    100% { stroke-dashoffset: 0; }
  }
  @keyframes navFillRise {
    0% { transform: translateY(100%); }
    10% { transform: translateY(100%); }
    40% { transform: translateY(0%); }
    100% { transform: translateY(0%); }
  }
`;


function AnimatedLogo({ size }) {
  const [isHovered, setIsHovered] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const { animationsEnabled } = useAnimationToggle();
  const timerRef = useRef(null);

  const handleMouseEnter = useCallback(() => {
    if (!animationsEnabled || isAnimating) return;
    setIsHovered(true);
    setIsAnimating(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setIsAnimating(false);
      setIsHovered(false);
    }, 1500);
  }, [animationsEnabled, isAnimating]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  if (!isHovered) {
    return (
      <Image
        width={size}
        src={pinpointLogo}
        alt="Pinpoint home"
        onMouseEnter={handleMouseEnter}
      />
    );
  }

  return (
    <>
      <Global styles={logoHoverStyles} />
      <Box w={size} h={size} onMouseEnter={handleMouseEnter}>
        <svg viewBox="0 0 512 512" width="100%" height="100%">
          <defs>
            <linearGradient id="nav-grad-outer" x1="131.26" y1="172.25" x2="380.74" y2="172.25" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#EC2227" />
              <stop offset="1" stopColor="#F15B27" />
            </linearGradient>
            <linearGradient id="nav-grad-pin" x1="131.88" y1="380.34" x2="241.58" y2="380.34" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#EC2227" />
              <stop offset="1" stopColor="#F15B27" />
            </linearGradient>
            <clipPath id="nav-fill-clip">
              <rect
                x="0" y="0" width={VB} height={VB}
                style={{ animation: `navFillRise ${DURATION} ease-in-out forwards` }}
              />
            </clipPath>
          </defs>
          <path
            d={OUTER_D}
            stroke="url(#nav-grad-outer)"
            style={{
              ...strokeStyle,
              strokeDasharray: OUTER_LEN,
              strokeDashoffset: OUTER_LEN,
              animation: `navDrawOuter ${DURATION} ease-in-out forwards`,
            }}
          />
          <path
            d={PIN_D}
            stroke="url(#nav-grad-pin)"
            style={{
              ...strokeStyle,
              strokeDasharray: PIN_LEN,
              strokeDashoffset: PIN_LEN,
              animation: `navDrawPin ${DURATION} ease-in-out forwards`,
            }}
          />
          <g clipPath="url(#nav-fill-clip)">
            <path d={OUTER_D} fill="url(#nav-grad-outer)" stroke="none" />
            <path d={PIN_D} fill="url(#nav-grad-pin)" stroke="none" />
          </g>
        </svg>
      </Box>
    </>
  );
}

export default function ProfileAppBar({ cancelPending = false, user, signoutLocation, title, subtitle, searchBar, compact = false, maxW = "container.xl" }) {
  const history = useHistory();
  const { isKiosk } = useKiosk();
  const toast = useAppToast();
  const [labPermission, setLabPermission] = useState([]);

  useEffect(() => {
    Backend.getLabPermissionsByUser(user.UID).then(({ items, status }) => {
      if (status === 200) {
        setLabPermission(items);
      } else {
        toast({ title: "Server call failed", status: "error", duration: 5000 });
      }
    });
  }, [user.UID]);

  const isAdmin = user.isSuperAdmin || labPermission.some((p) => p.is_admin);
  // Beta users can hop to the redesigned dashboard; never shown on kiosks.
  const showDashboard = useFeatureFlag(FRONTEND_V2_BETA).enabled && !isKiosk;

  return (
    <Box boxShadow="sm" bg="white">
      <Flex
        py={isKiosk ? 3 : 3}
        px={{ base: 4, md: 8 }}
        maxW={maxW}
        mx="auto"
        alignItems="center"
        flexWrap={isKiosk ? { base: "wrap", md: "nowrap" } : "nowrap"}
        gap={4}
      >
        {/* Left: logo + title group or standalone logo */}
        <Flex alignItems="center" gap={3} flexShrink={isKiosk ? 1 : 0} minW={0}>
          {isKiosk ? (
            <Link as={RouterLink} to={cancelPending ? "#" : kioskLocation("/kiosk", history.location)} onClick={event => { if (cancelPending) event.preventDefault(); }} display="flex" _hover={{ opacity: 0.8 }} _active={{ opacity: 0.7 }}>
              <Image boxSize={10} src={pinpointLogo} alt="Pinpoint home" />
            </Link>
          ) : (
            <Link as={RouterLink} to="/profile" display="flex" _active={{ opacity: 0.7 }}>
              <AnimatedLogo size="3em" />
            </Link>
          )}
          {title && (
            <Flex direction="column">
              <Text as={compact ? "h1" : "p"} fontSize={compact ? "2xl" : "lg"} fontWeight={compact ? 700 : 500} color={compact ? "gray.800" : "gray.600"} lineHeight="short">
                {title}
              </Text>
              {subtitle && (
                <Text fontSize="2xl" fontWeight={700} color="gray.800" lineHeight="short">
                  {subtitle}
                </Text>
              )}
            </Flex>
          )}
        </Flex>

        {/* Center: search bar fills available space */}
        {searchBar && (
          <Flex flex={1} justify="center" minW={0}>
            <Box w="full" maxW="320px">
              {searchBar}
            </Box>
          </Flex>
        )}

        {/* Right: actions — pushed to end */}
        <Flex alignItems="center" gap={3} ml="auto" flexShrink={0}>
          {showDashboard && (
            <Button
              variant="ghost"
              size="sm"
              fontWeight="500"
              color="gray.600"
              onClick={() => history.push("/dashboard")}
              isDisabled={cancelPending}
              aria-label="Go to the new dashboard"
              _hover={{ color: "gray.800", bg: "gray.100" }}
            >
              Dashboard
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="ghost"
              {...(isKiosk ? { size: "kiosk" } : { size: "sm" })}
              fontWeight={isKiosk ? 700 : "500"}
              color="gray.600"
              onClick={() => {
                if (isKiosk) localStorage.setItem('pinpoint_kiosk_mode', 'true');
                history.push("/admin");
              }}
              isDisabled={cancelPending}
              aria-label="Go to Adminland"
              _hover={{ color: "gray.800", bg: "gray.100" }}
            >
              Adminland
            </Button>
          )}

          {isKiosk ? (
            <Button
              size={compact ? "lg" : "kiosk"}
              minH="48px"
              variant="outline"
              borderWidth="2px"
              color="gray.700"
              isDisabled={cancelPending}
              onClick={() => { if (!cancelPending) history.push(kioskLocation(signoutLocation || "/kiosk", history.location)); }}
            >
              Cancel
            </Button>
          ) : (
            <Menu>
              <MenuButton
                as={Button}
                variant="ghost"
                p={0}
                rounded="full"
                _hover={{ opacity: 0.8 }}
                aria-label="User menu"
              >
                <Avatar
                  size="sm"
                  name={`${user.firstName} ${user.lastName}`}
                  src={getGravatarUrl(user.email)}
                  bg="brand.red"
                  color="white"
                  getInitials={(name) => {
                    const parts = name.split(" ").filter(Boolean);
                    return parts.map((p) => p[0]).join("").toUpperCase();
                  }}
                  sx={{
                    "& > div": {
                      transform: "translate(0.5px, 0.5px)",
                    },
                  }}
                />
              </MenuButton>
              <MenuList shadow="md" rounded="lg">
                <MenuItem onClick={() => history.push("/profile")}>
                  Profile
                </MenuItem>
                <MenuItem
                  onClick={() => history.push(kioskLocation(signoutLocation || "/logout", history.location))}
                  color="red.500"
                >
                  Sign Out
                </MenuItem>
              </MenuList>
            </Menu>
          )}
        </Flex>
      </Flex>
    </Box>
  );
}
